import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, CircularProgress, Switch, FormControlLabel, Button, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, Chip, Divider } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../tenantloader/firebase';
import { safeParseJson } from '@appesteem/aeshared-js/loadwatch';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import WarningIcon from '@mui/icons-material/Warning';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import AppsIcon from '@mui/icons-material/Apps';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import NoteIcon from '@mui/icons-material/Note';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { collection, query, orderBy, onSnapshot, doc, addDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../tenantloader/firebase';
import { useAuth } from '../AuthContext';
import { canSeeReport } from '../../../report/canSeeReport';
import AutowatchReportView, { extractReportJson } from '../../../report/AutowatchReportView';
import RightSidePanel from './RightSidePanel';

const VM_IFRAME_WIDTH = 1920;
const VM_IFRAME_HEIGHT = 1080;

function TakeControlButton({ readOnlyFromUrl, loadingRunData, currentUser, runId, runStatus, controlledBy, handleTakeControl, takingControl, instructionPanelMaximized }) {
  if (readOnlyFromUrl || loadingRunData || !currentUser || !runId || runStatus !== 'running') return null;
  if (controlledBy && controlledBy === currentUser.email) return null;

  return (
    <Button
      variant="contained"
      size="small"
      onClick={handleTakeControl}
      disabled={takingControl}
      sx={{
        ml: 1,
        minWidth: instructionPanelMaximized ? '90px' : '100px',
        fontSize: instructionPanelMaximized ? '10px' : '11px',
        padding: instructionPanelMaximized ? '2px 8px' : '4px 16px',
        textTransform: 'none',
        backgroundColor: 'primary.main',
        '&:hover': { backgroundColor: 'primary.dark' },
        '&:disabled': { backgroundColor: 'action.disabledBackground' },
      }}
    >
      {takingControl ? 'Taking Control...' : 'Take Control'}
    </Button>
  );
}

const LogViewer = ({ runId, readOnlyFromUrl = false, currentUserProp = null }) => {
  const authContext = useAuth() || {};
  const { currentUser: contextUser, autowatchServerUrl } = authContext;
  const currentUser = currentUserProp ?? contextUser;
  const showReport = canSeeReport(currentUser);
  const [status, setStatus] = useState('Connecting…');
  const [logs, setLogs] = useState([]);
  const [statusColor, setStatusColor] = useState('#0f0');
  const [screenshots, setScreenshots] = useState([]);
  const [currentScreenshotIndex, setCurrentScreenshotIndex] = useState(-1);
  const [logsPanelOpen, setLogsPanelOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [imgDimensions, setImgDimensions] = useState({ w: VM_IFRAME_WIDTH, h: VM_IFRAME_HEIGHT });
  // Reset to default dimensions when switching screenshots (avoids flash of wrong size)
  useEffect(() => {
    setImgDimensions({ w: VM_IFRAME_WIDTH, h: VM_IFRAME_HEIGHT });
  }, [currentScreenshotIndex]);
  const [autoNavigateToLatest, setAutoNavigateToLatest] = useState(true);
  const [logSearchQuery, setLogSearchQuery] = useState('');
  
  const [logTypeFilter, setLogTypeFilter] = useState(['info', 'warning', 'error', 'action']); // All types enabled by default
  const [controlledBy, setControlledBy] = useState(null);
  const [vmLink, setVmLink] = useState(null);
  const [runStatus, setRunStatus] = useState(null);
  const [doAutomation, setDoAutomation] = useState(false);
  const [isAzureVm, setIsAzureVm] = useState(false);
  const [reservedExpireAt, setReservedExpireAt] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [loadingRunData, setLoadingRunData] = useState(true);
  const [takingControl, setTakingControl] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Logs, 1 = VM, 2 = Report
  const [reportJson, setReportJson] = useState(null);      // null = not a JSON report (or not fetched), object = parsed report.json
  const [reportMd, setReportMd] = useState(null);          // null = not yet fetched, "" = not present, string = report.md content
  const [inspectionState, setInspectionState] = useState(null); // null = not fetched, false = not present, object = parsed JSON
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportUrl, setReportUrl] = useState(null);        // gs:// URL written by the producer, or null until upload completes
  const [inspectionUrl, setInspectionUrl] = useState(null);
  const [artifactIndex, setArtifactIndex] = useState(null); // { "screenshots/step9.png": "gs://..." } — used to resolve evidence paths in Report tab; null if run wasn't uploaded
  const [currentStep, setCurrentStep] = useState(null); // 'install', 'software', 'uninstall', or null
  const [releaseControlModalOpen, setReleaseControlModalOpen] = useState(false);
  const [uploadRunModalOpen, setUploadRunModalOpen] = useState(false);
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [doneModalOpen, setDoneModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [instructionPanelMaximized, setInstructionPanelMaximized] = useState(false);
  const logDivRef = useRef(null);
  const imageRef = useRef(null);
  const currentScreenshotIndexRef = useRef(-1);
  const autoNavigateToLatestRef = useRef(true);

  // Watch autowatchRun document for real-time updates to ControlledBy, VmLink, Status, DoAutomation, IsAzureVm, and ReservedExpireAt
  useEffect(() => {
    if (!runId) {
      setLoadingRunData(false);
      setControlledBy(null);
      setVmLink(null);
      setRunStatus(null);
      setDoAutomation(false);
      setIsAzureVm(false);
      setReservedExpireAt(null);
      setReportUrl(null);
      setInspectionUrl(null);
      setArtifactIndex(null);
      return;
    }

    setLoadingRunData(true);
    const runDocRef = doc(db, 'autowatchRun', runId);
    
    const unsubscribe = onSnapshot(
      runDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setControlledBy(data.ControlledBy || null);
          //setVmLink(data.VmLink || null);
          setRunStatus(data.Status || null);
          setDoAutomation(data.DoAutomation === true);
          setIsAzureVm(data.IsAzureVm === true);
          setReservedExpireAt(data.ReservedExpireAt || null);
          setReportUrl(data.AutowatchReportUrl || null);
          setInspectionUrl(data.AutowatchInspectionUrl || null);
          setArtifactIndex(data.AutowatchArtifactIndex || null);
          if (runId && db && !readOnlyFromUrl) {
            const secureVmRef = doc(db, 'autowatchRun', runId, 'secure', 'vm');
            getDoc(secureVmRef)
              .then((snap) => {
                const exists = snap.exists();
                const vmData = exists ? snap.data() : null;
                const link = vmData?.VmLink || null;
                setVmLink(link);
              })
              .catch((err) => {
                setVmLink(null);
              });
          } else {
            if (!runId || !db) console.log('[LogViewer] Skipping secure/vm: runId=', runId, 'db=', !!db);
            if (readOnlyFromUrl) console.log('[LogViewer] Skipping secure/vm: readOnlyFromUrl=true');
            setVmLink(null);
          }
          //console.log(data)
        } else {
          setControlledBy(null);
          setVmLink(null);
          setRunStatus(null);
          setDoAutomation(false);
          setIsAzureVm(false);
          setReservedExpireAt(null);
          setReportUrl(null);
          setInspectionUrl(null);
          setArtifactIndex(null);
        }
        setLoadingRunData(false);
      },
      (error) => {
        console.error('Error listening to autowatchRun data:', error);
        setControlledBy(null);
        setVmLink(null);
        setRunStatus(null);
        setDoAutomation(false);
        setIsAzureVm(false);
        setReservedExpireAt(null);
        setReportUrl(null);
        setInspectionUrl(null);
        setArtifactIndex(null);
        setLoadingRunData(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [runId, readOnlyFromUrl]);

  // Report tab is the last tab; its index depends on whether VM tab is hidden
  // (readOnlyFromUrl removes VM). Logs=0, VM=1 (if present), Report=last.
  const reportTabIndex = readOnlyFromUrl ? 1 : 2;

  // REPORT.md and autowatch-inspection.json locations are decided per-run by the
  // C# executor (UploadToFirebase.GetBlobPathForRun) — default-tenant vs custom-
  // tenant layouts differ, so we don't reconstruct the path here. The producer
  // writes the resulting `gs://` URLs into AutowatchReportUrl /
  // AutowatchInspectionUrl on the autowatchRun doc; consume those.
  useEffect(() => {
    if (activeTab !== reportTabIndex) return;
    if (loadingRunData) return; // wait for the run doc to settle
    if (!reportUrl && !inspectionUrl) {
      // Doc loaded but producer hasn't uploaded yet (live run, local mode, or
      // pre-synthesis failure). Show the not-available placeholders.
      setReportJson(null);
      setReportMd('');
      setInspectionState(false);
      setReportLoading(false);
      return;
    }
    let cancelled = false;
    setReportLoading(true);
    setReportError(null);
    // AutowatchReportUrl points at report.json (structured data); a legacy run may have a
    // report.md there instead. Fetch as text; extractReportJson parses JSON when present,
    // else null → render as markdown, else the not-available placeholder.
    const reportPromise = reportUrl
      ? getDownloadURL(storageRef(storage, reportUrl)).then((u) => fetch(u).then((r) => r.text()))
      : Promise.reject();
    const inspectionPromise = inspectionUrl
      ? getDownloadURL(storageRef(storage, inspectionUrl)).then((u) => fetch(u).then((r) => r.json()))
      : Promise.reject();
    Promise.allSettled([reportPromise, inspectionPromise]).then(([reportRes, inspRes]) => {
      if (cancelled) return;
      const reportText = reportRes.status === 'fulfilled' ? reportRes.value : '';
      const json = extractReportJson(reportText);
      setReportJson(json);                 // report.json object, or null
      setReportMd(json ? '' : reportText); // markdown only when it isn't a JSON report
      setInspectionState(inspRes.status === 'fulfilled' ? inspRes.value : false);
      setReportLoading(false);
    });
    return () => { cancelled = true; };
  }, [reportUrl, inspectionUrl, activeTab, reportTabIndex, loadingRunData]);

  useEffect(() => {
    // Clear logs and reset status when runId changes
    setLogs([]);
    setScreenshots([]);
    setCurrentScreenshotIndex(-1);
    setStatus('Connecting…');
    setStatusColor('#ff9800');

    if (!runId) {
      setStatus('Error: Missing runId');
      setStatusColor('#f00');
      return;
    }

    // Wait for run data to load before deciding what to show
    if (loadingRunData) {
      setStatus('Loading run data…');
      setStatusColor('#ff9800');
      return;
    }

    // Fetch logs from Firestore
    setStatus('Loading logs from Firestore…');
    setStatusColor('#ff9800');

    const logsRef = collection(db, 'autowatchRun', runId, 'logs');
    const q = query(logsRef, orderBy('Timestamp', 'asc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const firestoreLogs = [];
        const firestoreScreenshots = [];

        snapshot.forEach((doc) => {
          const logData = doc.data();
          const timestamp = logData.Timestamp;

            // Handle different log types
            if (logData.LogType) {
              const logType = logData.LogType;

              if (logType === 'screen' && logData.Data) {
                try {
                  const content = typeof logData.Data === 'string'
                    ? safeParseJson(logData.Data)
                    : logData.Data;
                  if (content) {
                    const screenshot = content.ScreenshotUrl || content.ScreenshotPath;
                    if (screenshot) {
                      const metadata = [
                        content.RunName && `Run: ${content.RunName}`,
                        content.StepName && `Step: ${content.StepName}`,
                        content.TaskName && `Task: ${content.TaskName}`
                      ].filter(Boolean);

                      const contextInfo = [
                        'Screen Observation',
                        content.ActiveWindowTitle && `Window: ${content.ActiveWindowTitle}`
                      ].filter(Boolean).join(' • ');

                      const descriptionParts = [
                        metadata.length > 0 ? metadata.join(' • ') : null,
                        contextInfo,
                        content.Description
                      ].filter(Boolean);

                      const description = descriptionParts.join('\n\n');

                      firestoreScreenshots.push({
                        url: screenshot,
                        timestamp: timestamp,
                        description: description,
                        type: 'screen',
                        content: content
                      });
                    }
                  }
                } catch (parseError) {
                  console.warn('Failed to process screen observation:', parseError);
                }
              } else if (logType === 'action' && logData.Data) {
                try {
                  const content = typeof logData.Data === 'string'
                    ? safeParseJson(logData.Data)
                    : logData.Data;
                  if (content) {
                    const textHint = content.TextHint || content.Description || 'Action performed';

                    firestoreLogs.push({
                      type: 'action',
                      content: textHint,
                      timestamp: timestamp,
                      id: doc.id
                    });

                    const startScreen = content.StartScreenObservation;
                    const screenshot = startScreen?.ScreenshotUrl || startScreen?.ScreenshotPath;

                    if (screenshot) {
                      const metadata = [
                        startScreen?.RunName && `Run: ${startScreen.RunName}`,
                        startScreen?.StepName && `Step: ${startScreen.StepName}`,
                        startScreen?.TaskName && `Task: ${startScreen.TaskName}`
                      ].filter(Boolean);

                      const contextInfo = [
                        'Action',
                        content.Type && `Type: ${content.Type}`,
                        content.TaskId && `Task ID: ${content.TaskId}`
                      ].filter(Boolean).join(' • ');

                      const actionDetails = [
                        content.Description && `Action: ${content.Description}`,
                        content.Reason && `Reason: ${content.Reason}`,
                        content.Expectation && `Expected: ${content.Expectation}`
                      ].filter(Boolean);

                      const descriptionParts = [
                        metadata.length > 0 ? metadata.join(' • ') : null,
                        contextInfo,
                        actionDetails.length > 0 ? actionDetails.join('\n\n') : null
                      ].filter(Boolean);

                      const description = descriptionParts.join('\n\n');

                      firestoreScreenshots.push({
                        url: screenshot,
                        timestamp: timestamp,
                        description: description,
                        type: 'action',
                        content: content
                      });
                    }
                  }
                } catch (parseError) {
                  console.warn('Failed to process action node:', parseError);
                }
              } else if (['info', 'warning', 'error'].includes(logType)) {
                const contentText = typeof logData.Data === 'string' ? logData.Data : JSON.stringify(logData.Data, null, 2);
                
                firestoreLogs.push({
                  type: logType,
                  content: contentText,
                  timestamp: timestamp,
                  id: doc.id
                });
              }
          }
        });

        setLogs(firestoreLogs);
        setScreenshots(prevScreenshots => {
          const newScreenshots = firestoreScreenshots;
          // Auto-navigate to latest screenshot if enabled
          if (autoNavigateToLatestRef.current && newScreenshots.length > 0) {
            const isViewingLatest = currentScreenshotIndexRef.current === prevScreenshots.length - 1 || 
                                    prevScreenshots.length === 0 || 
                                    currentScreenshotIndexRef.current === -1;
            if (isViewingLatest) {
              setCurrentScreenshotIndex(newScreenshots.length - 1);
            }
          } else if (prevScreenshots.length === 0 && newScreenshots.length > 0) {
            // Always show first screenshot when array is empty
            setCurrentScreenshotIndex(0);
          }
          return newScreenshots;
        });
        
        setStatus('Connected');
        setStatusColor('#4caf50');
      },
      (error) => {
        console.error('Error listening to Firestore logs:', error);
        setStatus('Error: Failed to load logs');
        setStatusColor('#f00');
      }
    );

    return () => {
      unsubscribe();
    };
  }, [runId, currentUser, loadingRunData]); // Removed controlledBy — it caused unnecessary listener restarts

  // Countdown timer for ReservedExpireAt
  useEffect(() => {
    if (!reservedExpireAt) {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      
      // Handle Firebase Timestamp - it has a toDate() method
      let expireTime;
      if (reservedExpireAt && typeof reservedExpireAt.toDate === 'function') {
        // It's a Firebase Timestamp object
        expireTime = reservedExpireAt.toDate().getTime();
      } else if (reservedExpireAt?.seconds) {
        // It's a Timestamp with seconds property
        expireTime = reservedExpireAt.seconds * 1000 + (reservedExpireAt.nanoseconds || 0) / 1000000;
      } else {
        // Try to parse as Date string or number
        expireTime = new Date(reservedExpireAt).getTime();
      }
      
      const diff = expireTime - now;

      if (diff <= 0) {
        setCountdown('Expiring soon');
        return;
      }

      const minutes = Math.floor(diff / (1000 * 60));
      
      // If less than 5 minutes, show "expiring soon"
      if (minutes < 5) {
        setCountdown('expiring soon');
        return;
      }

      // Otherwise show countdown in HH:MM:SS format
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [reservedExpireAt]);

  // Auto-switch to VM tab when control is taken or when VM becomes available
  useEffect(() => {
    if (!loadingRunData && currentUser && controlledBy && controlledBy === currentUser.email && vmLink) {
      //setActiveTab(1); // Switch to VM tab
    }
  }, [loadingRunData, currentUser, controlledBy, vmLink]);

  // Keep refs in sync with state
  useEffect(() => {
    currentScreenshotIndexRef.current = currentScreenshotIndex;
  }, [currentScreenshotIndex]);

  useEffect(() => {
    autoNavigateToLatestRef.current = autoNavigateToLatest;
    
    // When toggling Live ON, jump to latest screenshot and scroll logs to bottom
    if (autoNavigateToLatest && screenshots.length > 0 && currentScreenshotIndex < screenshots.length - 1) {
      setCurrentScreenshotIndex(screenshots.length - 1);
      
      // Scroll logs to bottom when switching to Live mode
      if (logDivRef.current) {
        setTimeout(() => {
          if (logDivRef.current) {
            logDivRef.current.scrollTop = logDivRef.current.scrollHeight;
          }
        }, 100);
      }
    }
  }, [autoNavigateToLatest, screenshots.length, currentScreenshotIndex]);

  // Auto-navigate to latest screenshot when run is selected
  useEffect(() => {
    if (screenshots.length > 0 && currentScreenshotIndex === -1) {
      setCurrentScreenshotIndex(screenshots.length - 1);
    }
  }, [screenshots.length, currentScreenshotIndex]);

  const currentScreenshot = currentScreenshotIndex >= 0 && currentScreenshotIndex < screenshots.length 
    ? screenshots[currentScreenshotIndex] 
    : null;

  // Reset loading when screenshot URL changes (if not already loading from user click)
  useEffect(() => {
    if (currentScreenshot) {
      // Pre-check if image is cached
      const testImg = new Image();
      testImg.onload = () => {
        // Image is cached, don't show loading
        if (!imageLoading) {
          setImageLoading(false);
        }
      };
      testImg.src = currentScreenshot.url;
      
      // If image is not complete (not cached), show loading
      if (!testImg.complete && !imageLoading) {
        setImageLoading(true);
      }
    }
  }, [currentScreenshot, imageLoading]);


  const handleTakeControl = async () => {
    if (!runId || !currentUser || takingControl) {
      return;
    }

    setTakingControl(true);
    try {
      // Create instruction document
      const instructionsRef = collection(db, 'autowatchRun', runId, 'instructions');
      await addDoc(instructionsRef, {
        Type: 'action',
        Text: 'TAKE_CONTROL',
        Timestamp: new Date().toISOString(),
        RequestedBy: currentUser.email,
        Status: "pending"
      });
      //send a fetch post request {}
      await fetch(`${autowatchServerUrl}/3c/vm/automation/link?autwatchRunId=${runId}`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      // Switch to VM tab when taking control
      //setActiveTab(1);
    } catch (error) {
      console.error('Error taking control:', error);
      setStatus('Error: Failed to take control');
      setStatusColor('#f00');
    } finally {
      setTakingControl(false);
    }
  };

  const handleSendInstruction = async (instruction) => {
    if (!runId || !currentUser) {
      throw new Error('Missing runId or currentUser');
    }

    const instructionsRef = collection(db, 'autowatchRun', runId, 'instructions');
    await addDoc(instructionsRef, instruction);
  };

  const handleStepSelect = async (stepName) => {
    if (!runId || !currentUser) return;

    let instructionText;
    const isActive = currentStep === stepName;

    if (stepName === 'install') {
      instructionText = isActive ? 'INSTALL_END' : 'INSTALL_START';
    } else if (stepName === 'software') {
      instructionText = isActive ? 'SOFTWARE_END' : 'SOFTWARE_START';
    } else if (stepName === 'uninstall') {
      instructionText = isActive ? 'UNINSTALL_END' : 'UNINSTALL_START';
    }

    try {
      await handleSendInstruction({
        Type: 'step',
        Text: instructionText,
        Timestamp: new Date().toISOString(),
        RequestedBy: currentUser.email,
        Status: 'pending'
      });

      // Update current step state
      setCurrentStep(isActive ? null : stepName);
    } catch (error) {
      console.error('Error sending step instruction:', error);
    }
  };

  const handleReleaseControl = async () => {
    if (!runId || !currentUser) return;

    try {
      await handleSendInstruction({
        Type: 'action',
        Text: 'RELEASE_CONTROL',
        Timestamp: new Date().toISOString(),
        RequestedBy: currentUser.email,
        Status: 'pending'
      });
      setReleaseControlModalOpen(false);
    } catch (error) {
      console.error('Error releasing control:', error);
    }
  };

  const handleUploadRun = async () => {
    if (!runId || !currentUser) return;

    try {
      await handleSendInstruction({
        Type: 'action',
        Text: 'UPLOAD',
        Timestamp: new Date().toISOString(),
        RequestedBy: currentUser.email,
        Status: 'pending'
      });
      setUploadRunModalOpen(false);
    } catch (error) {
      console.error('Error uploading run:', error);
    }
  };

  const handleExit = async () => {
    if (!runId || !currentUser) return;

    try {
      await handleSendInstruction({
        Type: 'action',
        Text: 'EXIT',
        Timestamp: new Date().toISOString(),
        RequestedBy: currentUser.email,
        Status: 'pending'
      });
      setExitModalOpen(false);
    } catch (error) {
      console.error('Error sending exit instruction:', error);
    }
  };

  const handleDone = async () => {
    if (!runId || !currentUser) return;

    try {
      await handleSendInstruction({
        Type: 'action',
        Text: 'DONE',
        Timestamp: new Date().toISOString(),
        RequestedBy: currentUser.email,
        Status: 'pending'
      });
      setDoneModalOpen(false);
    } catch (error) {
      console.error('Error sending done instruction:', error);
    }
  };

  const handleSendNote = async () => {
    if (!runId || !currentUser || !noteText.trim()) return;

    try {
      await handleSendInstruction({
        Type: 'note',
        Text: noteText.trim(),
        Timestamp: new Date().toISOString(),
        RequestedBy: currentUser.email,
        Status: 'pending'
      });
      setNoteText('');
      setNotesModalOpen(false);
    } catch (error) {
      console.error('Error sending note:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch (e) {
      return timestamp;
    }
  };

  const handlePrevious = () => {
    if (currentScreenshotIndex > 0) {
      // Switch to inspection mode when manually navigating
      setAutoNavigateToLatest(false);
      setImageLoading(true);
      setCurrentScreenshotIndex(currentScreenshotIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentScreenshotIndex < screenshots.length - 1) {
      // Switch to inspection mode when manually navigating
      setAutoNavigateToLatest(false);
      setImageLoading(true);
      setCurrentScreenshotIndex(currentScreenshotIndex + 1);
    }
  };

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth > 0 && naturalHeight > 0) {
      setImgDimensions({ w: naturalWidth, h: naturalHeight });
    }
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
  };

  const getActionClickStyle = () => {
    if (currentScreenshot?.type !== 'action' || imageLoading) {
      return { display: 'none' };
    }

    const action = currentScreenshot.content;
    if (!(action?.X || action?.x) || !(action?.Y || action?.y)) {
      return { display: 'none' };
    }

    const size = 22;
    const x = action.X || action.x || 0;
    const y = action.Y || action.y || 0;

    return {
      position: 'absolute',
      left: `${x - size / 2}px`,
      top: `${y - size / 2}px`,
      width: `${size}px`,
      height: `${size}px`,
      border: '2.5px solid #ffb300',
      borderRadius: '50%',
      background: 'rgba(255, 193, 7, 0.22)',
      boxShadow: '0 0 8px 2px rgba(255, 193, 7, 0.25)',
      boxSizing: 'border-box',
      pointerEvents: 'none',
      zIndex: 20,
      transition: 'left 0.08s, top 0.08s',
    };
  };

  const handleLogClick = (logTimestamp) => {
    if (screenshots.length === 0) return;
    
    // Switch to inspection mode (turn OFF Live toggle)
    setAutoNavigateToLatest(false);
    
    // Find the screenshot with timestamp closest to (but not after) the log timestamp
    const logTime = new Date(logTimestamp).getTime();
    let closestIndex = 0;
    let closestDiff = Infinity;
    
    for (let i = 0; i < screenshots.length; i++) {
      const screenshotTime = new Date(screenshots[i].timestamp).getTime();
      const diff = logTime - screenshotTime;
      
      // We want the latest screenshot before or at the log time
      if (diff >= 0 && diff < closestDiff) {
        closestDiff = diff;
        closestIndex = i;
      }
    }
    
    // If no screenshot found before the log, use the first one after
    if (closestDiff === Infinity) {
      for (let i = 0; i < screenshots.length; i++) {
        const screenshotTime = new Date(screenshots[i].timestamp).getTime();
        const diff = Math.abs(logTime - screenshotTime);
        
        if (diff < closestDiff) {
          closestDiff = diff;
          closestIndex = i;
        }
      }
    }
    
    setImageLoading(true);
    setCurrentScreenshotIndex(closestIndex);
  };

  // Toggle log type filter
  const toggleLogTypeFilter = (type) => {
    setLogTypeFilter(prev => {
      if (prev.includes(type)) {
        // Remove type from filter
        return prev.filter(t => t !== type);
      } else {
        // Add type to filter
        return [...prev, type];
      }
    });
  };

  // Filter logs based on search query and type filter
  const filteredLogs = logs.filter(log => {
    // Filter by type
    if (!logTypeFilter.includes(log.type)) return false;
    
    // Filter by search query
    if (!logSearchQuery) return true;
    const query = logSearchQuery.toLowerCase();
    return (
      log.content.toLowerCase().includes(query) ||
      log.type.toLowerCase().includes(query) ||
      formatTimestamp(log.timestamp).toLowerCase().includes(query)
    );
  });

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default', position: 'relative', overflow: 'hidden' }}>
      {/* Top bar with connection status, navigation, and logs toggle */}
      <Box
        sx={{
          ...(instructionPanelMaximized ? {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2001,
            height: '32px',
            padding: '0 12px'
          } : {
          height: '40px',
            padding: '0 16px'
          }),
          backgroundColor: 'background.default',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 100,
          flexShrink: 0
        }}
      >
        {/* Left: Connection status and Control info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: statusColor
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '12px' }}>
              {status}
            </Typography>
          </Box>
          
          {/* Countdown timer - always show if reservedExpireAt exists */}
          {reservedExpireAt && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1, borderLeft: 1, borderColor: 'divider' }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: countdown === 'expiring soon' || countdown === 'Expiring soon' ? 'warning.main' : 'text.secondary', 
                  fontSize: '11px', 
                  fontFamily: 'monospace',
                  fontWeight: 400
                }}
              >
                {countdown || 'calculating...'}
              </Typography>
            </Box>
          )}
          
          {/* Status and ControlledBy */}
          {!loadingRunData && runStatus && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, borderLeft: 1, borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                Status: <strong>{runStatus}</strong>
              </Typography>
              {runStatus === 'running' && controlledBy && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                  • Controlled by: <strong>{controlledBy === 'autowatch' ? 'Autowatch' : controlledBy}</strong>
                </Typography>
              )}
            </Box>
          )}
          
          <TakeControlButton
            readOnlyFromUrl={readOnlyFromUrl}
            loadingRunData={loadingRunData}
            currentUser={currentUser}
            runId={runId}
            runStatus={runStatus}
            controlledBy={controlledBy}
            handleTakeControl={handleTakeControl}
            takingControl={takingControl}
            instructionPanelMaximized={instructionPanelMaximized}
          />
          
          {/* Release Control button - show when controlled by current user and status is running */}
          {!readOnlyFromUrl && !loadingRunData && currentUser && runId && doAutomation && runStatus === 'running' && controlledBy && controlledBy === currentUser.email && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ExitToAppIcon sx={{ fontSize: instructionPanelMaximized ? '12px' : '14px' }} />}
              onClick={() => setReleaseControlModalOpen(true)}
              sx={{
                ml: 1,
                minWidth: instructionPanelMaximized ? 'auto' : '100px',
                fontSize: instructionPanelMaximized ? '10px' : '11px',
                padding: instructionPanelMaximized ? '2px 8px' : '4px 16px',
                textTransform: 'none',
                borderColor: 'warning.main',
                color: 'warning.main',
                '&:hover': {
                  borderColor: 'warning.dark',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              Release Control
            </Button>
          )}
        </Box>

        {/* Center: Navigation buttons and count */}
        {screenshots.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={handlePrevious}
              disabled={currentScreenshotIndex <= 0}
              sx={{ 
                color: 'primary.main',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'action.hover'
                },
                '&.Mui-disabled': {
                  color: 'action.disabled'
                }
              }}
            >
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px', minWidth: '60px', textAlign: 'center' }}>
              {currentScreenshotIndex + 1} of {screenshots.length}
            </Typography>
            <IconButton
              size="small"
              onClick={handleNext}
              disabled={currentScreenshotIndex >= screenshots.length - 1}
              sx={{ 
                color: 'primary.main',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'action.hover'
                },
                '&.Mui-disabled': {
                  color: 'action.disabled'
                }
              }}
            >
              <NavigateNextIcon fontSize="small" />
            </IconButton>
            <Box sx={{ 
              marginLeft: 1, 
              paddingLeft: 1, 
              borderLeft: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center'
            }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={autoNavigateToLatest}
                    onChange={(e) => setAutoNavigateToLatest(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'primary.main',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary', ml: 0.5 }}>
                    Live
                  </Typography>
                }
                sx={{ margin: 0 }}
              />
            </Box>
          </Box>
        )}

      </Box>


      {/* Queued / pending state — show friendly waiting message instead of tabs */}
      {!loadingRunData && (runStatus === 'queued' || runStatus === 'pending') && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
          <CircularProgress size={28} color="info" />
          <Typography variant="h6" color="text.secondary">Queued for inspection</Typography>
          <Typography variant="body2" color="text.disabled">
            This run is waiting to be picked up by an available machine. Results will appear here once inspection begins.
          </Typography>
        </Box>
      )}

      {/* Tabs and content - expand to fullscreen when maximized */}
      {(loadingRunData || (runStatus !== 'queued' && runStatus !== 'pending')) && <Box
        sx={{
          ...(instructionPanelMaximized ? {
            position: 'fixed',
            top: '64px', // Below top bar (32px) + tab bar (32px)
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1998, // Below tab bar (2000)
            backgroundColor: 'background.paper'
          } : {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          })
        }}
      >
        {(() => {
          return (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
            <Box sx={{ 
              ...(instructionPanelMaximized ? {
                position: 'fixed',
                top: '32px', // Right below the top bar
                left: 0,
                right: 0,
                zIndex: 2000
              } : {}),
              display: 'flex', 
              alignItems: 'center', 
              borderBottom: 1, 
              borderColor: 'divider', 
              flexShrink: 0,
              height: instructionPanelMaximized ? '32px' : 'auto',
              minHeight: instructionPanelMaximized ? '32px' : 'auto',
              backgroundColor: 'background.paper'
            }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ 
                  flex: 1, 
                  borderBottom: 'none',
                  minHeight: instructionPanelMaximized ? '32px' : 'auto',
                  '& .MuiTab-root': {
                    minHeight: instructionPanelMaximized ? '32px' : 'auto',
                    padding: instructionPanelMaximized ? '4px 8px' : '6px 12px'
                  }
                }}
              >
                <Tab 
                  label="Logs" 
                  sx={{ 
                    fontSize: instructionPanelMaximized ? '10px' : '11px',
                    textTransform: 'none'
                  }} 
                />
                {!readOnlyFromUrl && <Tab
                  label="VM"
                  sx={{
                    fontSize: instructionPanelMaximized ? '10px' : '11px',
                    textTransform: 'none'
                  }}
                />}
                {showReport && <Tab
                  label="Report"
                  sx={{
                    fontSize: instructionPanelMaximized ? '10px' : '11px',
                    textTransform: 'none'
                  }}
                />}
              </Tabs>
              
              {!readOnlyFromUrl && (
              <>
              {/* Step selection buttons */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5, 
                padding: '0 8px', 
                borderLeft: 1,
                borderColor: 'divider',
                opacity: (runStatus === 'running' && controlledBy === currentUser?.email) ? 1 : 0.5,
                pointerEvents: (runStatus === 'running' && controlledBy === currentUser?.email) ? 'auto' : 'none'
              }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', mr: 0.5 }}>
                  Step:
                </Typography>
                <Button
                  variant={currentStep === 'install' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<InstallDesktopIcon sx={{ fontSize: '14px' }} />}
                  onClick={() => handleStepSelect('install')}
                  disabled={runStatus !== 'running' || controlledBy !== currentUser?.email}
                  sx={{
                    padding: '2px 8px',
                    textTransform: 'none',
                    fontSize: '10px',
                    minWidth: 'auto',
                    borderColor: currentStep === 'install' ? 'primary.main' : 'divider',
                    backgroundColor: currentStep === 'install' ? 'primary.main' : 'transparent',
                    color: currentStep === 'install' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: currentStep === 'install' ? 'primary.dark' : 'action.hover',
                      borderColor: 'primary.main'
                    },
                    '&:disabled': {
                      borderColor: 'divider',
                      color: 'text.disabled',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  Install
                </Button>
                <Button
                  variant={currentStep === 'software' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<AppsIcon sx={{ fontSize: '14px' }} />}
                  onClick={() => handleStepSelect('software')}
                  disabled={runStatus !== 'running' || controlledBy !== currentUser?.email}
                  sx={{
                    padding: '2px 8px',
                    textTransform: 'none',
                    fontSize: '10px',
                    minWidth: 'auto',
                    borderColor: currentStep === 'software' ? 'primary.main' : 'divider',
                    backgroundColor: currentStep === 'software' ? 'primary.main' : 'transparent',
                    color: currentStep === 'software' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: currentStep === 'software' ? 'primary.dark' : 'action.hover',
                      borderColor: 'primary.main'
                    },
                    '&:disabled': {
                      borderColor: 'divider',
                      color: 'text.disabled',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  Software
                </Button>
                <Button
                  variant={currentStep === 'uninstall' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: '14px' }} />}
                  onClick={() => handleStepSelect('uninstall')}
                  disabled={runStatus !== 'running' || controlledBy !== currentUser?.email}
                  sx={{
                    padding: '2px 8px',
                    textTransform: 'none',
                    fontSize: '10px',
                    minWidth: 'auto',
                    borderColor: currentStep === 'uninstall' ? 'primary.main' : 'divider',
                    backgroundColor: currentStep === 'uninstall' ? 'primary.main' : 'transparent',
                    color: currentStep === 'uninstall' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: currentStep === 'uninstall' ? 'primary.dark' : 'action.hover',
                      borderColor: 'primary.main'
                    },
                    '&:disabled': {
                      borderColor: 'divider',
                      color: 'text.disabled',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  Uninstall
                </Button>
              </Box>
              
              {/* Action buttons and expand icon parallel to Tabs */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                padding: '0 16px', 
                borderLeft: 1,
                borderColor: 'divider',
                opacity: (runStatus === 'running' && controlledBy === currentUser?.email) ? 1 : 0.5,
                pointerEvents: (runStatus === 'running' && controlledBy === currentUser?.email) ? 'auto' : 'none'
              }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CloudUploadIcon sx={{ fontSize: '14px' }} />}
                  onClick={() => setUploadRunModalOpen(true)}
                  disabled={runStatus !== 'running' || controlledBy !== currentUser?.email}
                  sx={{
                    padding: '2px 8px',
                    textTransform: 'none',
                    fontSize: '10px',
                    backgroundColor: 'primary.main',
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: 'primary.dark'
                    },
                    '&:disabled': {
                      backgroundColor: 'action.disabledBackground',
                      color: 'text.disabled'
                    }
                  }}
                >
                  Upload Run
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<NoteIcon sx={{ fontSize: '14px' }} />}
                  onClick={() => setNotesModalOpen(true)}
                  disabled={runStatus !== 'running' || controlledBy !== currentUser?.email}
                  sx={{
                    padding: '2px 8px',
                    textTransform: 'none',
                    fontSize: '10px',
                    borderColor: 'secondary.main',
                    color: 'secondary.main',
                    minWidth: 'auto',
                    '&:hover': {
                      borderColor: 'secondary.dark',
                      backgroundColor: 'action.hover'
                    },
                    '&:disabled': {
                      borderColor: 'divider',
                      color: 'text.disabled'
                    }
                  }}
                >
                  Notes
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ExitToAppIcon sx={{ fontSize: '14px' }} />}
                  onClick={() => setExitModalOpen(true)}
                  disabled={runStatus !== 'running' || controlledBy !== currentUser?.email}
                  sx={{
                    padding: '2px 8px',
                    textTransform: 'none',
                    fontSize: '10px',
                    borderColor: 'error.main',
                    color: 'error.main',
                    minWidth: 'auto',
                    '&:hover': {
                      borderColor: 'error.dark',
                      backgroundColor: 'action.hover'
                    },
                    '&:disabled': {
                      borderColor: 'divider',
                      color: 'text.disabled'
                    }
                  }}
                >
                  Exit
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CheckCircleIcon sx={{ fontSize: '14px' }} />}
                  onClick={() => setDoneModalOpen(true)}
                  disabled={runStatus !== 'running' || controlledBy !== currentUser?.email}
                  sx={{
                    padding: '2px 8px',
                    textTransform: 'none',
                    fontSize: '10px',
                    borderColor: 'success.main',
                    color: 'success.main',
                    minWidth: 'auto',
                    '&:hover': {
                      borderColor: 'success.dark',
                      backgroundColor: 'action.hover'
                    },
                    '&:disabled': {
                      borderColor: 'divider',
                      color: 'text.disabled'
                    }
                  }}
                >
                  Done
                </Button>
                <IconButton
                  size="small"
                  onClick={() => setInstructionPanelMaximized(!instructionPanelMaximized)}
                  sx={{
                    padding: '4px',
                    color: 'primary.main',
                    pointerEvents: 'auto', // Always enabled
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  {instructionPanelMaximized ? (
                    <FullscreenExitIcon sx={{ fontSize: '18px' }} />
                  ) : (
                    <FullscreenIcon sx={{ fontSize: '18px' }} />
                  )}
                </IconButton>
              </Box>
              </>
              )}
              
              {/* Logs toggle - always enabled */}
        <Box
          onClick={() => setLogsPanelOpen(!logsPanelOpen)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '4px',
                  marginLeft: '8px',
            color: logsPanelOpen ? 'primary.main' : 'text.secondary',
            backgroundColor: logsPanelOpen ? 'action.selected' : 'transparent',
            transition: 'all 0.2s',
                  pointerEvents: 'auto', // Always enabled
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
                <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: logsPanelOpen ? 500 : 400 }}>
            Logs {logs.length > 0 && `(${logs.length})`}
          </Typography>
                {logsPanelOpen ? <ChevronRightIcon fontSize="small" sx={{ fontSize: '16px' }} /> : <ChevronLeftIcon fontSize="small" sx={{ fontSize: '16px' }} />}
        </Box>
      </Box>
            
            {/* Tab content */}
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
              {/* Logs tab content */}
              {activeTab === 0 && (
                <Box sx={{ 
                  flex: 1,
                  display: 'flex', 
                  flexDirection: 'column',
                  backgroundColor: 'background.paper',
                  transition: 'margin-right 0.3s ease',
                  marginRight: logsPanelOpen ? '400px' : '0',
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: 0,
                  height: '100%'
                }}>
        <Box
          sx={{
            flex: 1,
            padding: '16px',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            backgroundColor: 'background.default',
            position: 'relative',
            minHeight: 0
          }}
        >
          {currentScreenshot ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: '100%',
              height: '100%',
              gap: 2
            }}>
            <Box sx={{
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {imageLoading && (
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'background.paper',
                  opacity: 0.9,
                  zIndex: 10
                }}>
                  <CircularProgress size={40} />
                </Box>
              )}
              <Box sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                containerType: 'size',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '--sx': `calc(100cqw / ${imgDimensions.w}px)`,
                '--sy': `calc(100cqh / ${imgDimensions.h}px)`,
                '--s': 'min(var(--sx), var(--sy))'
              }}>
                <Box sx={{
                  width: `calc(${imgDimensions.w}px * var(--s))`,
                  height: `calc(${imgDimensions.h}px * var(--s))`,
                  overflow: 'hidden',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <Box sx={{
                    position: 'relative',
                    width: `${imgDimensions.w}px`,
                    height: `${imgDimensions.h}px`,
                    transformOrigin: '0 0',
                    transform: 'scale(var(--s))'
                  }}>
                    <img
                      ref={imageRef}
                      src={currentScreenshot.url}
                      alt={`Screen observation ${currentScreenshotIndex + 1}`}
                      style={{
                        width: `${imgDimensions.w}px`,
                        height: `${imgDimensions.h}px`,
                        display: 'block',
                        opacity: imageLoading ? 0 : 1,
                        transition: 'opacity 0.2s'
                      }}
                      onLoad={handleImageLoad}
                      onError={(e) => {
                        console.error('Failed to load screenshot:', currentScreenshot.url);
                        handleImageError();
                        e.target.style.display = 'none';
                      }}
                    />
                    {currentScreenshot?.type === 'action' && (
                      <div style={getActionClickStyle()} />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
              {currentScreenshot.description && (
                <Box
                  sx={{
                    flex: '0 0 auto',
                    width: '100%',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    padding: '12px',
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                    border: 1,
                    borderColor: 'divider'
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.primary',
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {currentScreenshot.description}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 4 }}>
              Waiting for screenshot...
            </Typography>
          )}
        </Box>
        </Box>
              )}
              
              {/* VM tab content */}
              {!readOnlyFromUrl && activeTab === 1 && (
                <Box sx={{ 
                  flex: 1,
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  position: 'relative',
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {(() => {
                    // Determine which message to show or show iframe
                    if (runStatus !== 'running') {
                      return (
                        <Box sx={{ 
                          flex: 1, 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 2,
                          padding: 4,
                          minHeight: 0
                        }}>
                          <WarningIcon sx={{ fontSize: '64px', color: 'warning.main', opacity: 0.7 }} />
                          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Cannot Access VM
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', maxWidth: '400px' }}>
                            AutoWatchRun is not active
                          </Typography>
                        </Box>
                      );
                    }
                    
                    if (controlledBy && controlledBy !== currentUser?.email) {
                      return (
                        <Box sx={{ 
                          flex: 1, 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 2,
                          padding: 4,
                          minHeight: 0
                        }}>
                          <WarningIcon sx={{ fontSize: '64px', color: 'warning.main', opacity: 0.7 }} />
                          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Cannot Access VM
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', maxWidth: '400px' }}>
                            It's not owned by you. Take Control
                          </Typography>
                        </Box>
                      );
                    }
                    
                    if (!isAzureVm) {
                      return (
                        <Box sx={{ 
                          flex: 1, 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 2,
                          padding: 4,
                          minHeight: 0
                        }}>
                          <WarningIcon sx={{ fontSize: '64px', color: 'warning.main', opacity: 0.7 }} />
                          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Cannot Access VM
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', maxWidth: '400px' }}>
                            Not an Azure VM. Cannot create VM Link
                          </Typography>
                        </Box>
                      );
                    }
                    
                    // Show iframe if all conditions are met (fixed 1920x1080, scale-to-fit via container queries, centered)
                    if (vmLink && controlledBy === currentUser?.email) {
                      return (
                  <Box
                    sx={{
                      flex: 1,
                      width: '100%',
                      minHeight: 0,
                      overflow: 'hidden',
                      containerType: 'size',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '--sx': `calc(100cqw / ${VM_IFRAME_WIDTH}px)`,
                      '--sy': `calc(100cqh / ${VM_IFRAME_HEIGHT + 100}px)`,
                      '--s': 'min(var(--sx), var(--sy))'
                    }}
                  >
                    <Box
                      sx={{
                        width: `calc(${VM_IFRAME_WIDTH}px * var(--s))`,
                        height: `calc(${VM_IFRAME_HEIGHT}px * var(--s))`,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${VM_IFRAME_WIDTH}px`,
                          height: `${VM_IFRAME_HEIGHT}px`,
                          transformOrigin: '0 0',
                          transform: 'scale(var(--s))'
                        }}
                      >
                        <iframe
                          src={vmLink}
                          style={{
                            width: `${VM_IFRAME_WIDTH}px`,
                            height: `${VM_IFRAME_HEIGHT}px`,
                            border: 'none',
                            display: 'block'
                          }}
                          title="VM Control"
                          allow="fullscreen"
                        />
                      </Box>
                    </Box>
                  </Box>
        );
                    }

                    // Default message
                    return (
        <Box sx={{ 
                        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        padding: 4,
                        minHeight: 0
                      }}>
                        <WarningIcon sx={{ fontSize: '64px', color: 'warning.main', opacity: 0.7 }} />
                        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          Cannot Access VM
            </Typography>
                        <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', maxWidth: '400px' }}>
                          VM Link is not available
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>
              )}

              {/* Report tab content — REPORT.md (markdown) + autowatch-inspection.json (structured) */}
              {showReport && activeTab === reportTabIndex && (
                <ReportTab
                  runId={runId}
                  reportUrl={reportUrl}
                  inspectionUrl={inspectionUrl}
                  artifactIndex={artifactIndex}
                  loading={reportLoading}
                  error={reportError}
                  reportJson={reportJson}
                  reportMd={reportMd}
                  inspectionState={inspectionState}
                />
              )}
            </Box>
          </Box>
          );
        })()}
        </Box>}

      {/* Right side panel with Logs tab */}
      <RightSidePanel
        open={logsPanelOpen}
        logs={logs}
        logSearchQuery={logSearchQuery}
        setLogSearchQuery={setLogSearchQuery}
        logTypeFilter={logTypeFilter}
        toggleLogTypeFilter={toggleLogTypeFilter}
        filteredLogs={filteredLogs}
        handleLogClick={handleLogClick}
        formatTimestamp={formatTimestamp}
        logDivRef={logDivRef}
        hasInstructionBar={controlledBy && controlledBy === currentUser?.email}
        isMaximized={instructionPanelMaximized}
      />

      {!readOnlyFromUrl && (
      <>
      {/* Release Control Modal */}
      <Dialog
        open={releaseControlModalOpen}
        onClose={() => setReleaseControlModalOpen(false)}
      >
        <DialogTitle>Release Control</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Control will be handed back to the machine. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseControlModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleReleaseControl} color="warning" variant="contained">
            Release Control
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Run Modal */}
      <Dialog open={uploadRunModalOpen} onClose={() => setUploadRunModalOpen(false)}>
        <DialogTitle>Upload Run</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Done with testing. This will stop the watch and upload the build. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadRunModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleUploadRun} color="primary" variant="contained">
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exit Modal */}
      <Dialog open={exitModalOpen} onClose={() => setExitModalOpen(false)}>
        <DialogTitle>Exit Process</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will stop the process and put the VM for cleanup. This action cannot be undone. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExitModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleExit} color="error" variant="contained">
            Exit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Done Modal */}
      <Dialog open={doneModalOpen} onClose={() => setDoneModalOpen(false)}>
        <DialogTitle>Mark as Completed</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will mark the run as completed. This will not upload any run, just mark the run as completed so that the VM can be claimed. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDoneModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDone} color="success" variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Modal */}
      <Dialog 
        open={notesModalOpen} 
        onClose={() => {
          setNotesModalOpen(false);
          setNoteText('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter your note here..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setNotesModalOpen(false);
              setNoteText('');
            }} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendNote} 
            color="primary" 
            variant="contained"
            disabled={!noteText.trim()}
          >
            Send Note
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    </Box>
  );
};

/**
 * ReportTab — Renders the run-end report in priority order: the structured report.json
 * (native MUI view) if present, else a legacy report.md (markdown), else a not-available
 * placeholder. The legacy autowatch-inspection.json state is shown below only when present.
 *
 * The report is written at run-end (after the agent loop finishes); during a live run it
 * doesn't exist yet, in which case we show an explanatory placeholder.
 */
function ReportTab({ runId, reportUrl, inspectionUrl, artifactIndex, loading, error, reportJson, reportMd, inspectionState }) {
  const hasReport = !!reportJson || (typeof reportMd === 'string' && reportMd !== '');
  return (
    <Box sx={{
      flex: 1,
      width: '100%',
      height: '100%',
      minHeight: 0,
      overflow: 'auto',
      padding: 3,
      backgroundColor: 'background.paper',
    }}>
      {!runId && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Report is not available — no runId resolved.
        </Typography>
      )}

      {runId && loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Loading report…
          </Typography>
        </Box>
      )}

      {runId && !loading && error && (
        <Typography variant="body2" sx={{ color: 'error.main' }}>
          Failed to load report: {error}
        </Typography>
      )}

      {/* 1) report.json — native structured view */}
      {runId && !loading && !error && reportJson && (
        <AutowatchReportView data={reportJson} />
      )}

      {/* 2) report.md — markdown fallback */}
      {runId && !loading && !error && !reportJson && reportMd && (
        <Box sx={{
          '& h1, & h2, & h3, & h4': { mt: 2, mb: 1 },
          '& h3': { fontSize: '1.15rem', fontWeight: 600 },
          '& h4': { fontSize: '1rem', fontWeight: 600, color: 'text.secondary' },
          '& p': { mb: 1, lineHeight: 1.5 },
          '& ul': { pl: 3, mb: 1 },
          '& li': { mb: 0.5 },
          '& code': {
            backgroundColor: 'action.hover',
            padding: '1px 4px',
            borderRadius: '3px',
            fontSize: '0.9em',
          },
          '& em': { color: 'text.secondary' },
          '& strong': { color: 'primary.main' },
        }}>
          <ReactMarkdown>{reportMd}</ReactMarkdown>
        </Box>
      )}

      {/* 3) neither — not available */}
      {runId && !loading && !error && !hasReport && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {reportUrl
            ? 'No report available — the report URL is set but the file could not be fetched or parsed.'
            : 'No report available yet. It is written at run-end; the run may still be in flight, the supervisor may be running in local mode (no GCS upload), or it may have failed before synthesis.'}
        </Typography>
      )}

      {/* Legacy autowatch-inspection.json state — only shown when present */}
      {runId && !loading && !error && inspectionState && typeof inspectionState === 'object' && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
            Inspection state
          </Typography>
          <InspectionStateView state={inspectionState} artifactIndex={artifactIndex} />
        </>
      )}
    </Box>
  );
}

function InspectionStateView({ state, artifactIndex }) {
  const summary = state.summarizedInfo || {};
  const failed = state.failedACRs || {};
  const verdicts = state.agentVerdicts || {};
  const phaseSummaries = state.phaseSummaries || {};
  const counts = state.llmCallCounts || {};
  const failedAcrIds = Object.keys(failed).sort();
  const fmtUsd = (v) => (typeof v === 'number' ? `$${v.toFixed(4)}` : '—');

  // Resolve every gs:// URL referenced by any verdict's evidence to an https
  // download URL once per inspection load. Map is { gsUrl: httpsUrl | false }.
  const [resolvedUrls, setResolvedUrls] = useState({});
  useEffect(() => {
    if (!artifactIndex) { setResolvedUrls({}); return; }
    const needed = new Set();
    for (const verdict of Object.values(state?.agentVerdicts || {})) {
      for (const ev of (verdict?.evidence || [])) {
        const gsUrl = artifactIndex[ev?.path];
        if (gsUrl) needed.add(gsUrl);
      }
    }
    if (needed.size === 0) { setResolvedUrls({}); return; }
    let cancelled = false;
    Promise.all([...needed].map(async (gsUrl) => {
      try {
        const httpsUrl = await getDownloadURL(storageRef(storage, gsUrl));
        return [gsUrl, httpsUrl];
      } catch {
        return [gsUrl, false];
      }
    })).then((pairs) => {
      if (cancelled) return;
      setResolvedUrls(Object.fromEntries(pairs));
    });
    return () => { cancelled = true; };
  }, [state, artifactIndex]);

  const anyEvidence = Object.values(verdicts).some((v) => (v?.evidence || []).length > 0);

  return (
    <Box>
      {/* Top-line summary chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {summary.appName && <Chip size="small" label={`App: ${summary.appName}`} />}
        {typeof summary.violationCount === 'number' && (
          <Chip size="small" color={summary.violationCount > 0 ? 'error' : 'success'}
                label={`${summary.violationCount} violation(s)`} />
        )}
        <Chip size="small" variant="outlined"
              label={`Total cost: ${fmtUsd(state.totalCostUSD)}`} />
        <Chip size="small" variant="outlined"
              label={`Extractor: ${fmtUsd(state.evidenceExtractorCostUSD)}`} />
        {typeof counts.agentVerdicts === 'number' && (
          <Chip size="small" variant="outlined"
                label={`Agent verdicts: ${counts.agentVerdicts}`} />
        )}
        {typeof counts.evidenceExtractor === 'number' && (
          <Chip size="small" variant="outlined"
                label={`Extractor calls: ${counts.evidenceExtractor}`} />
        )}
      </Box>

      {/* User saw the stop_and_upload step never ran (or no artifact index on
          the run doc) — show one banner instead of repeating "not uploaded"
          per evidence card. */}
      {!artifactIndex && anyEvidence && (
        <Box sx={{ mb: 2, p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Evidence files were not uploaded for this run — descriptions are shown below, but screenshots and artifacts cannot be opened.
          </Typography>
        </Box>
      )}

      {/* Failed ACRs grouped by acr id, listing per-phase entries */}
      {failedAcrIds.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Failed ACRs
          </Typography>
          {failedAcrIds.map((acrId) => {
            const rows = failed[acrId] || [];
            return (
              <Box key={acrId} sx={{ mb: 1.5, pl: 1, borderLeft: '3px solid', borderColor: 'error.main' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                  {acrId}
                  {rows.length > 1 && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      flagged in {rows.length} phases
                    </Typography>
                  )}
                </Typography>
                {rows.map((row, idx) => {
                  const verdict = verdicts[`${acrId}::${row.phase}`];
                  const evidence = verdict?.evidence || [];
                  const evValidation = verdict?.evidenceValidation || [];
                  return (
                    <Box key={idx} sx={{ mt: 0.5, ml: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                        {row.phase || 'unknown phase'}
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 0.5, lineHeight: 1.4 }}>
                        {row.reason || '(no reason recorded)'}
                      </Typography>
                      {evidence.length > 0 && (
                        <Box sx={{ mt: 1, ml: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {evidence.map((ev, eidx) => (
                            <EvidenceItem key={eidx} ev={ev}
                                          artifactIndex={artifactIndex}
                                          resolvedUrls={resolvedUrls} />
                          ))}
                        </Box>
                      )}
                      {evValidation.length > 0 && (
                        <Box sx={{ mt: 0.5, ml: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {evValidation.map((v, vidx) => (
                            <Chip key={vidx} size="small" color="warning" variant="outlined"
                                  label={v} sx={{ fontSize: '0.7rem', height: 20 }} />
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Phase summaries (agent's own consolidate summaries) */}
      {Object.keys(phaseSummaries).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Phase summaries
          </Typography>
          {['install', 'software', 'uninstall'].map((phase) =>
            phaseSummaries[phase] ? (
              <Box key={phase} sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                  {phase}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                  {phaseSummaries[phase]}
                </Typography>
              </Box>
            ) : null
          )}
        </Box>
      )}

      {/* Raw verdict count vs failed-ACR count (visibility into composite-key dict) */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          agentVerdicts entries: {Object.keys(verdicts).length} ·
          unique failed ACRs: {failedAcrIds.length} ·
          runId: {state.runId || '—'}
        </Typography>
      </Box>

      {/* Raw JSON, collapsed by default for the curious */}
      <details>
        <summary style={{ cursor: 'pointer', color: 'rgba(0,0,0,0.6)', fontSize: '0.85rem' }}>
          Show raw autowatch-inspection.json
        </summary>
        <Box component="pre" sx={{
          mt: 1, p: 1.5, backgroundColor: 'action.hover',
          fontSize: '0.75rem', overflow: 'auto', borderRadius: 1,
          fontFamily: 'monospace',
        }}>
          {JSON.stringify(state, null, 2)}
        </Box>
      </details>
    </Box>
  );
}

function EvidenceItem({ ev, artifactIndex, resolvedUrls }) {
  const path = ev?.path;
  const kind = ev?.kind;
  const what = ev?.what;
  const gsUrl = (artifactIndex && path) ? artifactIndex[path] : null;
  const httpsUrl = gsUrl ? resolvedUrls[gsUrl] : null;
  const placeholderText = !gsUrl
    ? (artifactIndex ? 'not uploaded' : 'uploads unavailable')
    : (httpsUrl === false ? 'resolution failed' : 'loading…');
  // In-app zoom — same UX as AutoInspectImage (dark Dialog, click-anywhere-closes).
  const [zoomOpen, setZoomOpen] = useState(false);

  return (
    <Box sx={{
      width: 220, p: 1, borderRadius: 1,
      border: '1px solid', borderColor: 'divider',
      backgroundColor: 'background.default',
    }}>
      {kind === 'screenshot' && (httpsUrl ? (
        <>
          <Box
            onClick={() => setZoomOpen(true)}
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'scale(1.02)', boxShadow: 3 },
            }}
          >
            <Box component="img" src={httpsUrl} alt={path}
                 sx={{ width: '100%', height: 110, objectFit: 'cover', display: 'block', borderRadius: 0.5 }} />
          </Box>
          <Dialog
            open={zoomOpen}
            onClose={() => setZoomOpen(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.9)', boxShadow: 'none', overflow: 'hidden' } }}
          >
            <DialogContent
              sx={{ p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', backgroundColor: 'transparent' }}
              onClick={() => setZoomOpen(false)}
            >
              <img src={httpsUrl} alt={path}
                   style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', cursor: 'pointer' }} />
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <Box sx={{
          width: '100%', height: 110, borderRadius: 0.5,
          backgroundColor: 'action.hover',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{placeholderText}</Typography>
        </Box>
      ))}

      {kind === 'artifact' && (
        <Box sx={{ py: 0.5 }}>
          {httpsUrl ? (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              <a href={httpsUrl} target="_blank" rel="noreferrer">{path}</a>
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
              {path}{' '}
              <Typography component="span" variant="caption">({placeholderText})</Typography>
            </Typography>
          )}
        </Box>
      )}

      {kind !== 'screenshot' && kind !== 'artifact' && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {kind || 'unknown'}: {path}
        </Typography>
      )}

      {what && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', lineHeight: 1.3 }}>
          {what}
        </Typography>
      )}
      <Typography variant="caption" sx={{
        display: 'block', mt: 0.25, fontFamily: 'monospace',
        fontSize: '0.65rem', color: 'text.disabled', wordBreak: 'break-all',
      }}>
        {kind}: {path}
      </Typography>
    </Box>
  );
}

export default LogViewer;

